/**
 * Authentication API Client
 */

import { API_BASE_URL } from '../config';

const API_BASE = API_BASE_URL;

export interface User {
    id: string;
    username: string;
    email: string;
    role: 'user' | 'admin';
    status: 'pending' | 'active' | 'suspended';
    createdAt: string;
    lastLogin: string | null;
}

export interface SearchLog {
    id: string;
    userId: string;
    username: string;
    searchedNumber: string;
    platform: 'whatsapp' | 'signal';
    timestamp: string;
}

interface AuthResponse {
    success: boolean;
    message: string;
    token?: string;
    user?: User;
}

interface SessionResponse {
    valid: boolean;
    user?: User;
    message?: string;
}

// Get stored token
export function getToken(): string | null {
    return localStorage.getItem('auth_token');
}

// Store token
export function setToken(token: string): void {
    localStorage.setItem('auth_token', token);
}

// Remove token
export function removeToken(): void {
    localStorage.removeItem('auth_token');
}

// Register a new user
export async function register(username: string, email: string, password: string): Promise<AuthResponse> {
    try {
        const response = await fetch(`${API_BASE}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, email, password })
        });
        
        return response.json();
    } catch (error) {
        console.error('Register fetch error:', error);
        return { success: false, message: 'Unable to connect to server. Please ensure the server is running.' };
    }
}

// Login
export async function login(username: string, password: string): Promise<AuthResponse> {
    try {
        const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });
        
        const data = await response.json();
        
        if (data.success && data.token) {
            setToken(data.token);
        }
        
        return data;
    } catch (error) {
        console.error('Login fetch error:', error);
        return { success: false, message: 'Unable to connect to server. Please ensure the server is running.' };
    }
}

// Logout
export async function logout(clearMessagingSessions: boolean = true): Promise<void> {
    const token = getToken();
    if (token) {
        await fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ clearMessagingSessions })
        });
    }
    removeToken();
}

// Get current user
export async function getCurrentUser(): Promise<SessionResponse> {
    const token = getToken();
    if (!token) {
        return { valid: false };
    }
    
    const response = await fetch(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
        removeToken();
        return { valid: false };
    }
    
    return response.json();
}

// Admin: Get all users
export async function getUsers(): Promise<{ users: User[] }> {
    const token = getToken();
    const response = await fetch(`${API_BASE}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch users');
    }
    
    return response.json();
}

// Admin: Update user status
export async function updateUserStatus(userId: string, status: 'pending' | 'active' | 'suspended'): Promise<{ success: boolean; user?: User }> {
    const token = getToken();
    const response = await fetch(`${API_BASE}/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
    });
    
    return response.json();
}

// Admin: Update user role
export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<{ success: boolean; user?: User }> {
    const token = getToken();
    const response = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role })
    });
    
    return response.json();
}

// Admin: Delete user
export async function deleteUser(userId: string): Promise<{ success: boolean; message: string }> {
    const token = getToken();
    const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    
    return response.json();
}

// Admin: Get search logs
export async function getSearchLogs(limit: number = 100): Promise<{ logs: SearchLog[] }> {
    const token = getToken();
    const response = await fetch(`${API_BASE}/admin/search-logs?limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch search logs');
    }
    
    return response.json();
}
